import { DateTime } from 'luxon';

/**
 * Compares two event arrays by length and by id + start.dateTime of each item.
 * Used for shallow equality checks (e.g. in useEffect) to avoid unnecessary updates.
 */

export function formatEventTime(
  start: string,
  end: string,
  timeZone: string,
  userTimeZone: string,
  isAllDay: boolean,
  locale?: string,
): string {
  if (isAllDay) {
    return 'All Day';
  }

  // Minutes are always shown, and 12- vs 24-hour follows the user's locale
  // rather than being hardcoded to 24-hour (SM-1274). 24-hour locales pad the
  // hour ("09:05"), while 12-hour locales read better unpadded ("9:05 AM").
  const hour12 = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12;
  const format = (dt: DateTime) =>
    dt.toLocaleString({ hour: hour12 ? 'numeric' : '2-digit', minute: '2-digit' }, { locale });

  const parseEventDateTime = (iso: string): DateTime => {
    const parsedWithZone = DateTime.fromISO(iso, { zone: timeZone });
    if (parsedWithZone.isValid) {
      return parsedWithZone;
    }

    const parsedWithoutZone = DateTime.fromISO(iso);
    return parsedWithoutZone;
  };

  const eventStart = parseEventDateTime(start);
  const eventEnd = parseEventDateTime(end);

  if (!(eventStart.isValid && eventEnd.isValid)) {
    return '';
  }

  const localStart = eventStart.setZone(userTimeZone);
  const localEnd = eventEnd.setZone(userTimeZone);

  const formattedStart = format(localStart);
  const formattedEnd = format(localEnd);

  return `${formattedStart} - ${formattedEnd}`;
}
